import { globalNotAuthorizedURL, globalNotFoundURL } from 'globalConstants';
import {
  getSessionStorageData,
  removeSessionStorageData,
  SessionStorageKeys,
  setSessionStorageData,
} from 'utils/sessionStorage';

//
// The page a signed-out user opened before the route guard sent them to the root to sign in, kept in session storage
// so that it survives a popup or a redirect sign-in of every provider, and opened once after the sign-in completes.
// Only a path of this app is kept: a value that is not a same-origin relative path is never stored or followed.
//

const maxReturnPathLength = 2048;

//the pages a sign-in never returns to: the root itself and the pages without a guard
const excludedReturnPaths = ['/', '/logout', globalNotFoundURL, globalNotAuthorizedURL];

// A path, query and fragment of this app, such as /approvals/<id>?tab=1: starts with one slash, no scheme, no
// authority, no backslash and no control character, and resolves to this origin
export function isSafeReturnPath(path: string | undefined): path is string {
  if (!path || path.length > maxReturnPathLength) return false;
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f\u007f]/.test(path)) return false;

  try {
    const url = new URL(path, window.location.origin);
    if (url.origin !== window.location.origin) return false;

    return !excludedReturnPaths.includes(url.pathname.toLowerCase());
  } catch {
    return false;
  }
}

//keeps the page to open after the sign-in; an unsafe or excluded path is ignored
export function rememberSignInReturnPath(path: string): void {
  if (!isSafeReturnPath(path)) return;

  setSessionStorageData(SessionStorageKeys.SignInReturnPath, path);
}

//the kept page, removed from storage; undefined when none is kept or the stored value is not safe
export function takeSignInReturnPath(): string | undefined {
  const path = getSessionStorageData(SessionStorageKeys.SignInReturnPath);
  clearSignInReturnPath();

  return isSafeReturnPath(path) ? path : undefined;
}

//removes the kept page only when it is this path: a page kept since by someone else (SignInRedirect) stays
export function forgetSignInReturnPath(path: string): void {
  if (getSessionStorageData(SessionStorageKeys.SignInReturnPath) === path) clearSignInReturnPath();
}

export function clearSignInReturnPath(): void {
  removeSessionStorageData(SessionStorageKeys.SignInReturnPath);
}
